import { useMemo, useState } from 'react'
import { com } from '@eidu/web'
import allSemanticLinkKeyConfigurations = com.eidu.entity.analytics.allSemanticLinkKeyConfigurations
import SemanticLinkKeyConfiguration = com.eidu.entity.analytics.SemanticLinkKeyConfiguration
import semanticLinkKeyFromString = com.eidu.entity.analytics.semanticLinkKeyFromString
import isAllowedNonPredefinedKey = com.eidu.entity.analytics.isAllowedNonPredefinedKey
import customSemanticLinkKeyPrefixes = com.eidu.entity.analytics.customSemanticLinkKeyPrefixes

const exampleKeys = customSemanticLinkKeyPrefixes
  .asJsReadonlyArrayView()
  .map((p) => `${p}my-key`)
  .join(' or ')

const useCustomSemanticLinkKey = (predefinedConfigs: readonly SemanticLinkKeyConfiguration[]) => {
  const [input, setInput] = useState('')
  const [error, setError] = useState<string | null>(null)

  const resolvedKey = useMemo(() => {
    if (predefinedConfigs.some((c) => c.key.toString() === input)) return null
    try {
      const key = semanticLinkKeyFromString(input)
      return isAllowedNonPredefinedKey(key) ? key : null
    } catch {
      return null
    }
  }, [predefinedConfigs, input])

  const change = (value: string) => {
    setInput(value)
    setError(null)
  }

  const validate = () => {
    if (!input) return
    if (predefinedConfigs.some((c) => c.key.toString() === input)) {
      setError('This key already exists as a predefined option — select it from the dropdown instead')
      return
    }
    try {
      const key = semanticLinkKeyFromString(input)
      setError(
        isAllowedNonPredefinedKey(key)
          ? null
          : `Keys in reserved namespaces must use a .custom. prefix, e.g. ${exampleKeys}`
      )
    } catch (e) {
      setError(`Invalid key: ${e instanceof Error ? e.message : String(e)}`)
    }
  }

  const reset = () => change('')

  return { input, error, resolvedKey, change, validate, reset }
}

export default useCustomSemanticLinkKey
export { exampleKeys }
export type CustomSemanticLinkKey = ReturnType<typeof useCustomSemanticLinkKey>
export const noPredefinedConfigs: readonly SemanticLinkKeyConfiguration[] = []
export const predefinedConfigsForKind = (kind: com.eidu.entity.type.EntityTypeKind) =>
  allSemanticLinkKeyConfigurations.asJsReadonlyArrayView().filter(({ onTypeKind }) => onTypeKind.equals(kind))
